import { Option } from '../../types';
import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from '../fetch';

export interface QuestionsParams extends PagingParams {
    subCategory: string;
}

export const useQuestions = (): FetchGet<
    PagingDataResponse<Question>,
    QuestionsParams
> => useFetchGet(
  'admin/questions',
  { autoStart: false, startStateLoading: false },
);

export type QuestionType = 'yes/no' | 'scale' | 'single choice' | 'multiple choice';
export type QuestionUserType = 'customer' | 'worker' | 'all';
export const QuestionUserTypeTitle: Record<QuestionUserType, string> = {
  customer: 'Customer',
  worker: 'Worker',
  all: 'All',
};

export type QuestionStatus = 'published' | 'deactivated' | 'draft';

export interface Question {
  id: string;
  name: string;
  description: string;
  scaleStart: number;
  scaleStartLabel: string;
  scaleEnd: number;
  scaleEndLabel: string;
  type: QuestionType;
  userType: QuestionUserType;
  status: QuestionStatus;
  singleChoiceList: Option<string>[];
  multipleChoiceList: Option<string>[];
  order: number;
  createdAt?: string;
  updatedAt?: string;
}

export const useQuestionById = (categoryId?: string): FetchGet<
    Question
> => useFetchGetId(
  'admin/questions',
  categoryId,
  { autoStart: false, startStateLoading: false },
);

export interface QuestionCreateResponse {
    id: string;
}

export interface QuestionCreateData {
    name: string;
    description: string;
    userType: QuestionUserType;
    order: number;
    scaleStart: number;
    scaleStartLabel: string;
    scaleEnd: number;
    scaleEndLabel: string;
    singleChoiceList: Option[];
    multipleChoiceList: Option[];
    type: QuestionType;
    status: QuestionStatus;
    subCategoryId: string;
}

export const useQuestionCreate = (): FetchCreate<
    QuestionCreateResponse,
    DefaultFetchError,
    QuestionCreateData
> => useFetchCreate(
  'admin/questions',
  { autoStart: false, startStateLoading: false },
);

export interface QuestionUpdateResponse extends QuestionCreateResponse {
    categoryId: string;
}

export type QuestionUpdateData = Partial<QuestionCreateData>;

export const useQuestionUpdate = (
  subCategoryId?: string,
): FetchUpdate<
    QuestionUpdateResponse,
    DefaultFetchError,
    QuestionUpdateData
> => useFetchUpdate(
  'admin/questions',
  subCategoryId,
  { autoStart: false, startStateLoading: false },
);

export const useQuestionDelete = (): FetchDelete => useFetchDelete(
  'admin/questions',
  '',
  { autoStart: false, startStateLoading: false },
);

export default {
  useQuestions,
  useQuestionById,
  useQuestionCreate,
  useQuestionUpdate,
};
